
import {View, Model} from 'backbone';
import template from 'dashboard/connections/details/connection-vectordb.ejs';
import NotificationService from 'NotificationService.js';
import RestService from 'RestService.js';
import SimpleConfirmModalView from '../../../common-views/SimpleConfirmModalView.js';
import { getLinkedApps, linkAppToVectorDB, unlinkAppFromVectorDB, bulkSyncAppToVectorDb, getSyncJobStatus, deleteVectorDbIndex } from '../../../../service/ai/api.js';

class ConnectionVectorDB extends View {
    initialize(opts){

        this.template = template;
        this.connectionService = opts.connectionService;

        this.events = {
            'change input[name=_apiKey],input[name=_indexName],input[name=_namespace],input[name=_clusterUrl],input[name=_collectionName],input[name=_chromaBaseUrl],input[name=_chromaTenant],input[name=_chromaDatabase],input[name=_zillizEndpoint],input[name=_zillizEmbeddingApiKey],input[name=_weaviateUrl],input[name=_weaviateOpenAiKey]': 'processConnectionInputs',
            'change select[name=_searchType]': 'processConnectionInputs',
            'change select[name=_provider]': 'onProviderChange',
            'change select[name=_weaviateVectorizer]': 'onVectorizerChange'
        };

        this.linkedApps = [];
        this._activeSyncs = new Map(); // formId -> intervalId

        this.listenTo(this.model, 'change', this.render);

        return this;
    }

    processConnectionInputs(){
        return this.connectionService.processConnectionInputs.apply(this.connectionService, arguments);
    }

    onProviderChange(ev){
        this.processConnectionInputs(ev);
        const provider = this.$el.find('select[name=_provider]').val();
        this.$el.find('.vectordb-pinecone-field').toggle(provider === 'pinecone');
        this.$el.find('.vectordb-qdrant-field').toggle(provider === 'qdrant');
        this.$el.find('.vectordb-chroma-field').toggle(provider === 'chroma');
        this.$el.find('.vectordb-zilliz-field').toggle(provider === 'zilliz');
        this.$el.find('.vectordb-zilliz-info').toggle(provider === 'zilliz');
        this.$el.find('.vectordb-weaviate-field').toggle(provider === 'weaviate');
        $('#connectionExtras').find('.vectordb-danger-zone').toggle(provider === 'pinecone');
        // Weaviate OpenAI key: only if vectorizer = text2vec-openai
        if (provider === 'weaviate') {
            const vectorizer = this.$el.find('select[name=_weaviateVectorizer]').val();
            this.$el.find('.vectordb-weaviate-openai-field').toggle(vectorizer === 'text2vec-openai');
        } else {
            this.$el.find('.vectordb-weaviate-openai-field').hide();
        }
        // Update index/collection label
        this.$el.find('.vectordb-index-label').text(provider === 'pinecone' ? 'Index Name' : 'Collection Name');
        // Chroma: disable search type (always semantic)
        const $searchType = this.$el.find('select[name=_searchType]');
        $searchType.prop('disabled', provider === 'chroma');
        if (provider === 'chroma') $searchType.val('semantic');
        // Zilliz: remove keyword option, keep semantic + hybrid
        const $keywordOpt = $searchType.find('option[value=keyword]');
        const $hybridOpt = $searchType.find('option[value=hybrid]');
        if (provider === 'zilliz' || provider === 'chroma') {
            $keywordOpt.hide();
            if ($searchType.val() === 'keyword') $searchType.val('semantic');
        } else {
            $keywordOpt.show();
        }
        if (provider === 'chroma') {
            $hybridOpt.hide();
        } else {
            $hybridOpt.show();
        }
    }

    onVectorizerChange(ev){
        this.processConnectionInputs(ev);
        const vectorizer = this.$el.find('select[name=_weaviateVectorizer]').val();
        this.$el.find('.vectordb-weaviate-openai-field').toggle(vectorizer === 'text2vec-openai');
    }

    getConnectionId(){
        return this.model.get('connectionId') || null;
    }

    async loadLinkedApps(){
        const connectionId = this.getConnectionId();
        if (!connectionId) return;

        const $list = $('#connectionExtras').find('#vectordbLinkedAppsList');
        $list.html('<div class="vectordb-loading">Loading linked apps...</div>');

        try {
            const response = await getLinkedApps(connectionId);
            this.linkedApps = Array.isArray(response) ? response : [];
            this.renderLinkedAppsList();
            this._resumePersistedJobs();
        } catch (err) {
            console.error('Error loading linked apps:', err);
            $list.html('<div class="vectordb-empty">Failed to load linked apps.</div>');
        }
    }

    renderLinkedAppsList(){
        const $list = $('#connectionExtras').find('#vectordbLinkedAppsList');

        if (!this.linkedApps || this.linkedApps.length === 0) {
            $list.html('<div class="vectordb-empty">No apps linked yet. Click "Link App" to start syncing data.</div>');
            $('#connectionExtras').find('button[name=linkNewApp]').show();
            return;
        }

        const html = this.linkedApps.map(app => {
            const syncInfo = this._formatSyncInfo(app.syncedInstances, app.lastSyncedAt);
            return `
            <div class="vectordb-linked-app-row" data-form-id="${app.formId}">
                <div class="vectordb-app-info">
                    <span class="vectordb-app-name">${this.escapeHtml(app.formName)}</span>
                    ${app.liveSync ? '<span class="vectordb-live-badge">Live Sync</span>' : ''}
                    <span class="vectordb-sync-info">${syncInfo}</span>
                </div>
                <div class="vectordb-app-actions">
                    <button class="btn btn-default btn-xs vectordb-sync-btn" data-form-id="${app.formId}" title="Sync instances to vector database">
                        <i class="glyphicon glyphicon-refresh"></i> Sync
                    </button>
                    <button class="btn btn-default btn-xs vectordb-unlink-btn" data-form-id="${app.formId}" data-form-name="${this.escapeHtml(app.formName)}" title="Unlink this app">
                        <i class="glyphicon glyphicon-remove"></i> Unlink
                    </button>
                </div>
            </div>`;
        }).join('');

        $list.html(html);

        // Limit: 1 app per connection — hide Link App button when already linked
        const $extras = $('#connectionExtras');
        $extras.find('button[name=linkNewApp]').toggle(this.linkedApps.length === 0);

        // Rebind events on dynamically rendered buttons
        const self = this;
        $list.find('.vectordb-sync-btn').on('click', (e) => self.syncApp(e));
        $list.find('.vectordb-unlink-btn').on('click', (e) => self.unlinkApp(e));

        // Restore progress bars for any active OR persisted syncs (so there's no flash of buttons)
        const connectionId = this.getConnectionId();
        const persistedFormIds = connectionId ? Object.keys(this._getPersistedJobs(connectionId)) : [];
        const formIdsToRestore = new Set([...Array.from(this._activeSyncs.keys()), ...persistedFormIds]);
        formIdsToRestore.forEach((formId) => {
            const $row = $list.find(`.vectordb-linked-app-row[data-form-id="${formId}"]`);
            if ($row.length) {
                $row.find('.vectordb-app-actions').html(this._getProgressBarHtml('Syncing...', 0));
            }
        });
    }

    escapeHtml(str) {
        if (!str) return '';
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    async showLinkAppDialog(){
        const connectionId = this.getConnectionId();
        if (!connectionId) {
            console.error('No connectionId found');
            return;
        }

        try {
            const response = await RestService.getAllApps();
            const appData = response.RESULT.RESPONSE.AppListSummary || response.RESULT.RESPONSE.AppList;
            const allApps = appData.app || appData.App;
            const apps = Array.isArray(allApps) ? allApps : [allApps];

            // Filter out already linked apps
            const linkedFormIds = this.linkedApps.map(a => String(a.formId));
            const availableApps = apps.filter(a => !linkedFormIds.includes(String(a['@id'] || a['@FormId'])));

            if (availableApps.length === 0) {
                NotificationService.info('All apps are already linked to this connection.');
                return;
            }

            const optionsHtml = availableApps.map(a =>
                `<option value="${a['@id'] || a['@FormId']}" data-guid="${a['@linkId'] || a['@FormGUID'] || ''}">${this.escapeHtml(a['@name'] || a['@Name'])}</option>`
            ).join('');

            const dialogHtml = `
                <div class="vectordb-link-dialog-overlay">
                    <div class="vectordb-link-dialog">
                        <div class="vectordb-link-dialog-header">
                            <strong>Link App to Vector Database</strong>
                        </div>
                        <div class="vectordb-link-dialog-body">
                            <label>Select an app:</label>
                            <select id="vectordbAppSelect" class="form-control">${optionsHtml}</select>
                        </div>
                        <div class="vectordb-link-dialog-buttons">
                            <button class="btn btn-primary btn-sm" id="vectordbConfirmLink">Link App</button>
                            <button class="btn btn-default btn-sm" id="vectordbCancelLink">Cancel</button>
                        </div>
                    </div>
                </div>`;

            $('body').append(dialogHtml);

            const self = this;

            $('#vectordbConfirmLink').on('click', async function(){
                const $select = $('#vectordbAppSelect');
                const formId = $select.val();
                const formGUID = $select.find(':selected').data('guid') || '';
                const formName = $select.find(':selected').text();

                $(this).prop('disabled', true).text('Linking...');

                try {
                    await linkAppToVectorDB(connectionId, formId, formGUID, formName);
                    $('.vectordb-link-dialog-overlay').remove();
                    NotificationService.success(`"${formName}" linked successfully.`);
                    self.loadLinkedApps();
                } catch (err) {
                    console.error('Error linking app:', err);
                    NotificationService.error('Failed to link app: ' + (err.message || 'Unknown error'));
                    $(this).prop('disabled', false).text('Link App');
                }
            });

            $('#vectordbCancelLink').on('click', function(){
                $('.vectordb-link-dialog-overlay').remove();
            });

        } catch (err) {
            console.error('Error loading apps:', err);
            NotificationService.error('Failed to load apps list.');
        }
    }

    async unlinkApp(e){
        const formId = $(e.currentTarget).data('form-id');
        const formName = $(e.currentTarget).data('form-name');
        const connectionId = this.getConnectionId();

        if (!confirm(`Unlink "${formName}" from this vector database? This will not delete existing vectors.`)) {
            return;
        }

        const $btn = $(e.currentTarget);
        $btn.prop('disabled', true);

        try {
            await unlinkAppFromVectorDB(connectionId, formId);
            NotificationService.success(`"${formName}" unlinked.`);
            this.loadLinkedApps();
        } catch (err) {
            console.error('Error unlinking app:', err);
            NotificationService.error('Failed to unlink app: ' + (err.message || 'Unknown error'));
            $btn.prop('disabled', false);
        }
    }

    async syncApp(e){
        const formId = String($(e.currentTarget).data('form-id'));
        const connectionId = this.getConnectionId();

        // Prevent double-click if already syncing this app
        if (this._activeSyncs.has(formId)) return;

        const $row = $('#connectionExtras').find(`.vectordb-linked-app-row[data-form-id="${formId}"]`);
        const $actions = $row.find('.vectordb-app-actions');

        // Replace buttons with progress bar
        $actions.html(this._getProgressBarHtml('Starting sync...', 0));

        try {
            const result = await bulkSyncAppToVectorDb(connectionId, formId);
            const jobId = result.jobId;
            const totalInstances = result.totalInstances || 0;

            // Update progress bar with initial total
            this._updateSyncProgress($row, { processedInstances: 0, totalInstances });

            // Persist so we can resume polling after navigation
            this._persistJob(connectionId, formId, jobId);

            this._startPolling(connectionId, formId, jobId);

        } catch (err) {
            console.error('Error starting sync:', err);
            NotificationService.error('Sync failed to start: ' + (err.message || 'Unknown error'));
            this.renderLinkedAppsList();
        }
    }

    _startPolling(connectionId, formId, jobId) {
        if (this._activeSyncs.has(formId)) return;

        const intervalId = setInterval(async () => {
            const $row = $('#connectionExtras').find(`.vectordb-linked-app-row[data-form-id="${formId}"]`);
            try {
                const status = await getSyncJobStatus(jobId);
                this._updateSyncProgress($row, status);

                if (status.status === 'completed') {
                    this._clearSync(formId);
                    this._clearPersistedJob(connectionId, formId);
                    const msg = `Sync complete: ${status.totalInstances} instances, ${status.totalVectors} vectors${status.errorCount > 0 ? ', ' + status.errorCount + ' errors' : ''}`;
                    NotificationService.success(msg);
                    this.loadLinkedApps();
                    if (status.totalVectors > 0 && this.getProvider() === 'pinecone') {
                        $('#connectionExtras').find('.vectordb-danger-zone').show();
                    }
                } else if (status.status === 'failed') {
                    this._clearSync(formId);
                    this._clearPersistedJob(connectionId, formId);
                    NotificationService.error('Sync failed: ' + (status.error || 'Unknown error'));
                    this.loadLinkedApps();
                }
            } catch (pollErr) {
                console.error('Polling error:', pollErr);
                this._clearSync(formId);
                this._clearPersistedJob(connectionId, formId);
                NotificationService.error('Sync status check failed: ' + (pollErr.message || 'Unknown error'));
                this.loadLinkedApps();
            }
        }, 2500);

        this._activeSyncs.set(formId, intervalId);
    }

    _jobStorageKey(connectionId) {
        return `vectordb_sync_jobs_${connectionId}`;
    }

    _getPersistedJobs(connectionId) {
        try {
            return JSON.parse(sessionStorage.getItem(this._jobStorageKey(connectionId)) || '{}');
        } catch (e) {
            return {};
        }
    }

    _persistJob(connectionId, formId, jobId) {
        const jobs = this._getPersistedJobs(connectionId);
        jobs[formId] = jobId;
        sessionStorage.setItem(this._jobStorageKey(connectionId), JSON.stringify(jobs));
    }

    _clearPersistedJob(connectionId, formId) {
        const jobs = this._getPersistedJobs(connectionId);
        delete jobs[formId];
        if (Object.keys(jobs).length === 0) {
            sessionStorage.removeItem(this._jobStorageKey(connectionId));
        } else {
            sessionStorage.setItem(this._jobStorageKey(connectionId), JSON.stringify(jobs));
        }
    }

    _resumePersistedJobs() {
        const connectionId = this.getConnectionId();
        if (!connectionId) return;
        const jobs = this._getPersistedJobs(connectionId);
        Object.entries(jobs).forEach(([formId, jobId]) => {
            if (!this._activeSyncs.has(String(formId))) {
                this._startPolling(connectionId, String(formId), jobId);
            }
        });
    }

    _getProgressBarHtml(text, pct) {
        return `<div class="vectordb-sync-progress">
            <div class="vectordb-sync-progress-bar-container">
                <div class="vectordb-sync-progress-bar" style="width: ${pct}%"></div>
            </div>
            <span class="vectordb-sync-progress-text"><i class="glyphicon glyphicon-refresh glyphicon-spin"></i> ${text}</span>
        </div>`;
    }

    _updateSyncProgress($row, status) {
        const $bar = $row.find('.vectordb-sync-progress-bar');
        const $text = $row.find('.vectordb-sync-progress-text');
        const total = status.totalInstances || 0;
        const processed = status.processedInstances || 0;
        const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
        $bar.css('width', pct + '%');
        const progressStr = total > 0
            ? `${processed.toLocaleString()} / ${total.toLocaleString()}`
            : 'Syncing...';
        $text.html(`<i class="glyphicon glyphicon-refresh glyphicon-spin"></i> ${progressStr}`);
    }

    _formatSyncInfo(syncedInstances, lastSyncedAt) {
        const count = syncedInstances || 0;
        const countStr = `${count} synced`;
        if (!lastSyncedAt) return `${countStr} · Never`;
        return `${countStr} · Last: ${this._formatTimeAgo(lastSyncedAt)}`;
    }

    _formatTimeAgo(dateStr) {
        // Ensure UTC parsing — append Z if no timezone info present
        const normalized = dateStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr) ? dateStr : dateStr + 'Z';
        const date = new Date(normalized);
        const now = new Date();
        const diffMs = now - date;
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1) return 'just now';
        if (diffMin < 60) return `${diffMin} min ago`;
        const diffHours = Math.floor(diffMin / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays}d ago`;
    }

    _clearSync(formId) {
        const key = String(formId);
        const intervalId = this._activeSyncs.get(key);
        if (intervalId) {
            clearInterval(intervalId);
            this._activeSyncs.delete(key);
        }
    }

    _clearAllSyncs() {
        this._activeSyncs.forEach((intervalId) => clearInterval(intervalId));
        this._activeSyncs.clear();
    }

    stopListening() {
        this._clearAllSyncs();
        super.stopListening();
    }

    async deleteIndex(){
        const connectionId = this.getConnectionId();
        if (!connectionId) {
            NotificationService.error('No connection selected.');
            return;
        }

        const confirmView = new SimpleConfirmModalView({
            model: new Model({
                confirmationText: 'Are you sure you want to delete the vector database index? This will permanently remove all indexed data and cannot be undone.',
                subtitle: 'Delete Index',
                height: 'auto',
                width: 400
            })
        });

        const result = await confirmView.render();
        if (result !== 'Ok') return;

        const $extras = $('#connectionExtras');
        const $btn = $extras.find('button[name=deleteIndex]');
        $btn.prop('disabled', true).html('<i class="glyphicon glyphicon-refresh glyphicon-spin"></i> Deleting...');

        try {
            const response = await deleteVectorDbIndex(connectionId);
            NotificationService.success(response.message || 'Vector database index deleted successfully.');
            $extras.find('.vectordb-danger-zone').hide();
            this.loadLinkedApps();
        } catch (err) {
            console.error('Error deleting vector DB index:', err);
            NotificationService.error('Failed to delete index: ' + (err.message || 'Unknown error'));
            $btn.prop('disabled', false).html('<i class="glyphicon glyphicon-trash"></i> Delete Index');
        }
    }

    getProvider(){
        return this.model.get('currentConnectionDetails')?.ConnectionXML?.Connection?.['_provider'] || 'pinecone';
    }

    renderExtras(){
        const $extras = $('#connectionExtras');
        if (!$extras.length) return;

        const provider = this.getProvider();
        const connectionId = this.getConnectionId();
        const showDangerZone = provider === 'pinecone' && !!connectionId;

        const html = `
            <div class="block vectordb-linked-apps-section">
                <h2>Linked Apps</h2>
                <div class="vectordb-linked-apps-list" id="vectordbLinkedAppsList">
                    <div class="vectordb-loading">Loading linked apps...</div>
                </div>
                <div class="vectordb-linked-apps-actions">
                    <button class="btn btn-default btn-sm" name="linkNewApp">
                        <i class="fa pf-add"></i> Link App
                    </button>
                </div>
            </div>
            <div class="block vectordb-danger-zone" style="${showDangerZone ? '' : 'display:none'}">
                <h2>Danger Zone</h2>
                <div class="vectordb-danger-zone-content">
                    <div class="vectordb-danger-zone-item">
                        <div class="vectordb-danger-zone-info">
                            <strong>Delete Index</strong>
                            <p>Permanently delete all vectors from the database index. This cannot be undone.</p>
                        </div>
                        <button class="btn btn-danger btn-sm" name="deleteIndex">
                            <i class="glyphicon glyphicon-trash"></i> Delete Index
                        </button>
                    </div>
                </div>
            </div>`;

        $extras.html(html);

        // Bind events on extras container (outside this.$el)
        const self = this;
        $extras.find('button[name=linkNewApp]').on('click', () => self.showLinkAppDialog());
        $extras.find('.vectordb-sync-btn').on('click', (e) => self.syncApp(e));
        $extras.find('.vectordb-unlink-btn').on('click', (e) => self.unlinkApp(e));
        $extras.find('button[name=deleteIndex]').on('click', () => self.deleteIndex());
    }

    render() {
        this.$el.empty();
        if(this.template) {
            this.$el.html(this.template(this.model.toJSON()));
        }
        this.delegateEvents();

        // Render linked apps and danger zone in the right column
        this.renderExtras();
        this.loadLinkedApps();

        return this;
    }
}

export default ConnectionVectorDB;
